import {
  Box,
  Typography,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { osirisTokens } from '@/theme/tokens';

interface FieldLabelProps {
  children: ReactNode;
}

/** Figma text-field label: IBM Plex Sans 13, txt-muted, above the field. */
export function FieldLabel({ children }: FieldLabelProps) {
  return (
    <Typography
      sx={{
        fontFamily: osirisTokens.fonts.body,
        fontSize: 13,
        letterSpacing: '0.02em',
        color: osirisTokens.text.muted,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

export interface SelectFieldOption {
  value: string;
  label: ReactNode;
}

interface SelectFieldProps {
  label?: string;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options: SelectFieldOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  multiple?: boolean;
  renderValue?: (selected: unknown) => ReactNode;
  className?: string;
  /** Override the check marker independently of the selected value; null marks no option. */
  checkedValue?: string | null;
}

/**
 * Standard 32px select (Figma text field S) — field chrome (height, border,
 * focus color) comes from the theme; this adds the label/helper wiring so
 * forms don't hand-roll FormControl + Select.
 */
export function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  error,
  helperText,
  fullWidth = true,
  multiple = false,
  renderValue,
  className,
  checkedValue,
}: SelectFieldProps) {
  // Multi-selects render selected values as small chips (Figma tags)
  const defaultMultiRenderValue = (selected: unknown) => {
    const sel = selected as string[];
    // empty state: same badge as the tags, not italic text (Figma 28786:74278)
    if (!sel || sel.length === 0) return <Chip size="small" label={placeholder} />;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {sel.map((v) => (
          <Chip key={v} size="small" label={options.find((o) => o.value === v)?.label ?? v} />
        ))}
      </Box>
    );
  };

  // Single selects must render only the option label in the closed field —
  // without this MUI mirrors the MenuItem children, check icon included
  const defaultSingleRenderValue = (selected: unknown) => {
    const v = selected as string;
    if (!v) {
      return placeholder ? (
        <Box component="span" sx={{ color: osirisTokens.text.tertiary }}>
          {placeholder}
        </Box>
      ) : (
        ''
      );
    }
    return options.find((o) => o.value === v)?.label ?? v;
  };

  return (
    <Box className={className} sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <FormControl fullWidth={fullWidth} error={error}>
        <Select
          value={value ?? (multiple ? [] : '')}
          onChange={(e) => onChange(e.target.value as string | string[])}
          displayEmpty
          disabled={disabled}
          multiple={multiple}
          renderValue={
            renderValue ?? (multiple ? defaultMultiRenderValue : defaultSingleRenderValue)
          }
          sx={{ fontFamily: osirisTokens.fonts.body, fontSize: 14 }}
        >
          {placeholder && !multiple && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )}
          {options.map((opt) => {
            const isSelected = multiple
              ? ((value as string[]) ?? []).includes(opt.value)
              : value === opt.value;
            const isChecked = checkedValue === undefined ? isSelected : checkedValue === opt.value;
            return (
              <MenuItem key={opt.value} value={opt.value} sx={{ gap: 1.5 }}>
                {/* Check marks are a multi-select affordance (Figma dropdowns);
                    single selects show one only for an explicit checkedValue
                    marker, reserving the slot so labels stay aligned. */}
                {(multiple || checkedValue !== undefined) && (
                  <Icon
                    name="check"
                    size={20}
                    sx={{
                      color: isChecked ? osirisTokens.icons.activeLight : osirisTokens.icons.muted,
                      visibility: checkedValue !== undefined && !isChecked ? 'hidden' : 'visible',
                    }}
                  />
                )}
                {opt.label}
              </MenuItem>
            );
          })}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
}
